import { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Lock, Unlock, Search } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'manager',
    organizationId: '',
    supervisorId: '',
    bypassTraining: false,
  });

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Подтвердить',
    isLoading: false,
  });

  useEffect(() => {
    loadUsers();
    loadOrganizations();
  }, [roleFilter]);

  const loadUsers = async () => {
    try {
      const params = {};
      if (roleFilter !== 'all') params.role = roleFilter;

      const response = await api.get('/api/admin/users', { params });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Ошибка загрузки пользователей');
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      const response = await api.get('/api/admin/organizations');
      setOrganizations(response.data.organizations || []);
    } catch (error) {
      console.error('Error loading organizations:', error);
    }
  };

  const loadSupervisors = async (organizationId) => {
    if (!organizationId) {
      setSupervisors([]);
      return;
    }
    try {
      const response = await api.get('/api/admin/users', {
        params: { role: 'supervisor' }
      });
      // Filter by organization
      const orgSupervisors = response.data.users.filter(
        u => u.organization_id === organizationId
      );
      setSupervisors(orgSupervisors);
    } catch (error) {
      console.error('Error loading supervisors:', error);
      setSupervisors([]);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      role: 'manager',
      organizationId: '',
      supervisorId: '',
    });
    setSupervisors([]);
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      role: user.role,
      organizationId: user.organization_id || '',
      supervisorId: user.supervisor_id || '',
      bypassTraining: user.bypass_training || false,
    });
    // Load supervisors for this organization
    if (user.organization_id) {
      loadSupervisors(user.organization_id);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields for new users
    if (!editingUser) {
      if (!formData.firstName || !formData.firstName.trim()) {
        toast.error('Имя обязательно для заполнения');
        return;
      }

      if (!formData.lastName || !formData.lastName.trim()) {
        toast.error('Фамилия обязательна для заполнения');
        return;
      }

      // Validate password requirements
      if (!formData.password) {
        toast.error('Пароль обязателен');
        return;
      }

      if (formData.password.length < 8) {
        toast.error('Пароль должен быть минимум 8 символов');
        return;
      }

      if (!/[A-Z]/.test(formData.password)) {
        toast.error('Пароль должен содержать хотя бы одну заглавную букву');
        return;
      }

      if (!/[a-z]/.test(formData.password)) {
        toast.error('Пароль должен содержать хотя бы одну строчную букву');
        return;
      }

      if (!/[0-9]/.test(formData.password)) {
        toast.error('Пароль должен содержать хотя бы одну цифру');
        return;
      }
    }

    // Validate organization for non-admin roles
    if (formData.role !== 'admin' && !formData.organizationId) {
      console.error('Validation failed: Organization required for role:', formData.role);
      toast.error('Организация обязательна для этой роли');
      return;
    }

    // Validate supervisor for managers
    if (formData.role === 'manager' && !formData.supervisorId) {
      console.error('Validation failed: Supervisor required for manager');
      toast.error('Супервайзер обязателен для менеджеров');
      return;
    }

    console.log('Validation passed, submitting form...');

    try {
      if (editingUser) {
        // Update user
        const updateData = {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role,
          organizationId: formData.organizationId || null,
          supervisorId: formData.role === 'manager' ? (formData.supervisorId || null) : null,
          bypassTraining: formData.bypassTraining,
        };

        console.log('Updating user with data:', updateData);
        await api.put(`/api/admin/users/${editingUser.id}`, updateData);
        toast.success('Пользователь обновлен');
      } else {
        // Create user - send all required fields
        const userData = {
          email: formData.email.trim(),
          password: formData.password,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          role: formData.role,
          organizationId: formData.organizationId,
          supervisorId: formData.role === 'manager' ? formData.supervisorId : null,
          bypassTraining: formData.bypassTraining,
        };

        console.log('Creating user with data:', { ...userData, password: '***' });

        await api.post('/api/admin/users', userData);
        toast.success('Пользователь создан');
      }

      setShowModal(false);
      loadUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      const errorMessage = error.response?.data?.error || 'Ошибка сохранения пользователя';
      toast.error(errorMessage);
    }
  };

  const handleToggleActive = (user) => {
    setConfirmDialog({
      isOpen: true,
      title: user.is_active ? 'Заблокировать пользователя' : 'Разблокировать пользователя',
      message: `Вы уверены, что хотите ${user.is_active ? 'заблокировать' : 'разблокировать'} пользователя ${user.first_name} ${user.last_name} (${user.email})?`,
      confirmText: user.is_active ? 'Заблокировать' : 'Разблокировать',
      isLoading: false,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isLoading: true }));
        try {
          await api.patch(`/api/admin/users/${user.id}/toggle-active`);
          toast.success(user.is_active ? 'Пользователь заблокирован' : 'Пользователь разблокирован');
          loadUsers();
          setConfirmDialog(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
          console.error('Error toggling user status:', error);
          toast.error('Ошибка изменения статуса');
          setConfirmDialog(prev => ({ ...prev, isLoading: false }));
        }
      },
    });
  };

  const handleDeleteUser = (user) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Удалить пользователя',
      message: `Вы уверены, что хотите удалить пользователя ${user.first_name} ${user.last_name} (${user.email})? Это действие нельзя отменить.`,
      confirmText: 'Удалить',
      isLoading: false,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isLoading: true }));
        try {
          await api.delete(`/api/admin/users/${user.id}`);
          toast.success('Пользователь удален');
          loadUsers();
          setConfirmDialog(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
          console.error('Error deleting user:', error);
          toast.error(error.response?.data?.error || 'Ошибка удаления');
          setConfirmDialog(prev => ({ ...prev, isLoading: false }));
        }
      },
    });
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: 'bg-purple-100 text-purple-700',
      director: 'bg-indigo-100 text-indigo-700',
      supervisor: 'bg-blue-100 text-blue-700',
      manager: 'bg-green-100 text-green-700',
    };
    const labels = {
      admin: 'Администратор',
      director: 'Директор',
      supervisor: 'Супервайзер',
      manager: 'Менеджер',
    };
    return { class: badges[role] || badges.manager, label: labels[role] || role };
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.first_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.last_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Управление пользователями</h1>
          <p className="text-gray-600 mt-1">Всего пользователей: {users.length}</p>
        </div>
        <button
          onClick={handleCreateUser}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Добавить пользователя
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Поиск по email или имени..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Все роли</option>
          <option value="admin">Администратор</option>
          <option value="director">Директор</option>
          <option value="supervisor">Супервайзер</option>
          <option value="manager">Менеджер</option>
        </select>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Пользователь
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Роль
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Организация
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Руководитель
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Статус
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Действия
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredUsers.map((user) => {
              const roleBadge = getRoleBadge(user.role);
              return (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-gray-900">
                        {user.first_name && user.last_name
                          ? `${user.first_name} ${user.last_name}`
                          : user.email}
                      </p>
                      {user.first_name && <p className="text-sm text-gray-500">{user.email}</p>}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${roleBadge.class}`}>
                      {roleBadge.label}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {user.organization_id ? (
                      <span className="text-sm text-gray-900">
                        {organizations.find(o => o.id === user.organization_id)?.name || 'Не найдена'}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {user.supervisor_id ? (
                      (() => {
                        const supervisor = users.find(u => u.id === user.supervisor_id);
                        return supervisor ? (
                          <span className="text-sm text-gray-900">
                            {supervisor.first_name} {supervisor.last_name}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-500">Не найден</span>
                        );
                      })()
                    ) : (
                      <span className="text-sm text-gray-500">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {user.is_active ? (
                      <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700">
                        Активен
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-700">
                        Заблокирован
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Редактировать"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleActive(user)}
                        className={`p-2 rounded-lg transition-colors ${
                          user.is_active
                            ? 'text-orange-600 hover:bg-orange-50'
                            : 'text-green-600 hover:bg-green-50'
                        }`}
                        title={user.is_active ? 'Заблокировать' : 'Разблокировать'}
                      >
                        {user.is_active ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Удалить"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredUsers.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            Пользователи не найдены
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {editingUser ? 'Редактировать пользователя' : 'Создать пользователя'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Пароль <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    minLength={8}
                    placeholder="Минимум 8 символов, A-z, 0-9"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Требования: минимум 8 символов, заглавная буква, строчная буква, цифра
                  </p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Имя {!editingUser && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  required={!editingUser}
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Фамилия {!editingUser && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  required={!editingUser}
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Роль</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="manager">Менеджер</option>
                  <option value="supervisor">Супервайзер</option>
                  <option value="director">Директор</option>
                  <option value="admin">Администратор</option>
                </select>
              </div>
              {formData.role !== 'admin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Организация {formData.role !== 'admin' && '*'}
                  </label>
                  <select
                    value={formData.organizationId}
                    onChange={(e) => {
                      const orgId = e.target.value;
                      setFormData({ ...formData, organizationId: orgId, supervisorId: '' });
                      if (formData.role === 'manager') {
                        loadSupervisors(orgId);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Выберите организацию</option>
                    {organizations.filter(org => org.is_active).map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                  {organizations.length === 0 && (
                    <p className="text-sm text-amber-600 mt-1">
                      ⚠️ Сначала создайте организацию
                    </p>
                  )}
                </div>
              )}
              {formData.role === 'manager' && formData.organizationId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Супервайзер *
                  </label>
                  <select
                    value={formData.supervisorId}
                    onChange={(e) => setFormData({ ...formData, supervisorId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Выберите супервайзера</option>
                    {supervisors.map(sup => (
                      <option key={sup.id} value={sup.id}>
                        {sup.first_name} {sup.last_name} ({sup.email})
                      </option>
                    ))}
                  </select>
                  {supervisors.length === 0 && (
                    <p className="text-sm text-amber-600 mt-1">
                      ⚠️ Сначала создайте супервайзера для этой организации
                    </p>
                  )}
                </div>
              )}
              {/* Checkbox для обхода обучения */}
              <div className="flex items-center space-x-2 mt-4">
                <input
                  type="checkbox"
                  id="bypassTraining"
                  checked={formData.bypassTraining}
                  onChange={(e) => setFormData({ ...formData, bypassTraining: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="bypassTraining" className="text-sm font-medium text-gray-700">
                  Обойти обучение
                </label>
                <span className="text-xs text-gray-500">
                  (доступ к сценариям без прохождения обучения)
                </span>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingUser ? 'Сохранить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        isLoading={confirmDialog.isLoading}
      />

    </div>
  );
};

export default UserManagement;
